import {
  collection,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, storage, functions } from "../firebase";

// Data sources for the Services and Categories pages. Both countries use the
// same pages (same UI); only where the data comes from differs:
// - Australia: the AU database and bucket directly (the original behaviour).
// - India: India admin callables only (admin check happens on the server).

const listDocs = async (colRef) => {
  const qs = await getDocs(query(colRef, orderBy("createdAt", "desc")));
  const items = [];
  qs.forEach((d) => items.push({ id: d.id, ...d.data() }));
  return items;
};

export const auCatalog = {
  country: "AU",
  currencySymbol: "$",
  // Same as `a || b || c`, which the Australian pages have always used.
  firstSet: (...values) => values.find(Boolean) ?? values[values.length - 1],

  getPlatformSettings: async () => {
    const docSnap = await getDoc(doc(db, "platformSettings", "main"));
    return docSnap.exists() ? docSnap.data() : null;
  },
  listServices: () => listDocs(collection(db, "services")),
  listAllServices: async () => {
    const qs = await getDocs(collection(db, "services"));
    const items = [];
    qs.forEach((d) => items.push({ id: d.id, ...d.data() }));
    return items;
  },
  listCategories: () => listDocs(collection(db, "categories")),
  listSubcategories: (categoryId) =>
    listDocs(collection(db, "categories", categoryId, "subcategories")),

  uploadServiceImage: async (file) => {
    const imgRef = storageRef(storage, `service_images/service_${Date.now()}_${file.name}`);
    await uploadBytes(imgRef, file);
    return getDownloadURL(imgRef);
  },
  createService: async (data) => (await addDoc(collection(db, "services"), data)).id,
  updateService: (id, data) => updateDoc(doc(db, "services", id), data),
  deleteService: (id) => deleteDoc(doc(db, "services", id)),

  uploadCategoryImage: async (file, imagePath) => {
    const imgRef = storageRef(storage, imagePath);
    await uploadBytes(imgRef, file);
    return { imageUrl: await getDownloadURL(imgRef), imagePath };
  },
  createCategory: async (data) => (await addDoc(collection(db, "categories"), data)).id,
  updateCategory: (id, data) => updateDoc(doc(db, "categories", id), data),
  deleteCategory: (id) => deleteDoc(doc(db, "categories", id)),
  createSubcategory: async (categoryId, data) =>
    (await addDoc(collection(db, "categories", categoryId, "subcategories"), data)).id,
  updateSubcategory: (categoryId, id, data) =>
    updateDoc(doc(db, "categories", categoryId, "subcategories", id), data),
  deleteSubcategory: (categoryId, id) =>
    deleteDoc(doc(db, "categories", categoryId, "subcategories", id)),
};

const callIndia = async (name, payload = {}) => {
  try {
    const result = await httpsCallable(functions, name)(payload);
    return result.data || {};
  } catch (error) {
    // A bare "internal" means the function could not be reached at all.
    if (error?.code === "functions/internal" && error?.message === "internal") {
      throw new Error("India admin service is not reachable. Please try again later.");
    }
    throw error;
  }
};

// One callable returns the whole India catalogue; pages ask for parts of it
// in parallel, so reuse a recent answer and drop it after every change.
const CACHE_MS = 5000;
let indiaCache = null;
let indiaCacheAt = 0;
const loadIndiaCatalog = () => {
  if (!indiaCache || Date.now() - indiaCacheAt > CACHE_MS) {
    indiaCacheAt = Date.now();
    indiaCache = callIndia("adminListIndiaCatalog").catch((error) => {
      indiaCache = null;
      throw error;
    });
  }
  return indiaCache;
};
const changed = (result) => {
  indiaCache = null;
  return result;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const uploadIndiaImage = async (folder, file) =>
  callIndia("adminUploadIndiaCatalogImage", {
    folder,
    fileName: file.name,
    contentType: file.type,
    base64: await readFileAsBase64(file),
  });

const isSet = (value) =>
  value !== undefined && value !== null && value !== "" && !Number.isNaN(value);

export const indiaCatalog = {
  country: "IN",
  currencySymbol: "₹",
  // India commission and platform fee may be 0, so 0 counts as a value.
  firstSet: (...values) => values.find(isSet) ?? values[values.length - 1],

  getPlatformSettings: async () => (await loadIndiaCatalog()).platformSettings || null,
  // Payout Settings page: the server keeps only the India fields (fixed platform fee).
  defaultPlatformSettings: {
    defaultCommissionRate: 0,
    commissionType: "percentage",
    platformFee: 0,
    platformFeeType: "fixed",
    providerAcceptanceWindowMinutes: 30,
    minimumPayout: 0,
    payoutFrequency: "weekly",
  },
  savePlatformSettings: async (settings) =>
    changed((await callIndia("adminSaveIndiaPlatformSettings", { settings })).platformSettings),
  listServices: async () => (await loadIndiaCatalog()).services || [],
  listAllServices: async () => (await loadIndiaCatalog()).services || [],
  listCategories: async () =>
    ((await loadIndiaCatalog()).categories || []).map(({ subcategories, ...category }) => category),
  listSubcategories: async (categoryId) =>
    ((await loadIndiaCatalog()).categories || []).find((c) => c.id === categoryId)
      ?.subcategories || [],

  uploadServiceImage: async (file) =>
    changed((await uploadIndiaImage("service_images", file)).imageUrl),
  createService: async (data) =>
    changed((await callIndia("adminSaveIndiaService", { data })).id),
  updateService: async (id, data) =>
    changed(await callIndia("adminSaveIndiaService", { serviceId: id, data })),
  deleteService: async () => {
    throw new Error("Deleting India services is not available.");
  },

  // The server picks the India path; the Australian path is ignored.
  uploadCategoryImage: async (file) => changed(await uploadIndiaImage("categories", file)),
  createCategory: async (data) =>
    changed((await callIndia("adminSaveIndiaCategory", { data })).id),
  updateCategory: async (id, data) =>
    changed(await callIndia("adminSaveIndiaCategory", { categoryId: id, data })),
  deleteCategory: async () => {
    throw new Error("Deleting India categories is not available.");
  },
  createSubcategory: async (categoryId, data) =>
    changed((await callIndia("adminSaveIndiaSubcategory", { categoryId, data })).id),
  updateSubcategory: async (categoryId, id, data) =>
    changed(
      await callIndia("adminSaveIndiaSubcategory", { categoryId, subcategoryId: id, data })
    ),
  deleteSubcategory: async (categoryId, id) =>
    changed(await callIndia("adminDeleteIndiaSubcategory", { categoryId, subcategoryId: id })),
};
